/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect, useCallback, forwardRef } from 'react';
import { Spinner, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import api from '../../../api';
import ShopHOC from '../../../hoc/ShopHOC';
import CartContents from '../Cart/CartContents/CartContents';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { updateOrder, partnerFetch } from "../../../actions";
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import LoadingOverlay from '../../common/LoadingOverlay';
import usePrevious from '../../../hooks/usePrevious';
import { toastrAdd } from '../../../actions/index';

function Checkout(props) {
    const { eventID, cartOrder, orderRowsMeta, removeProductFromCart, setProductQuantity, partnerPortalSettings, cart, portalClosed  } = props;
    const { userCanEditOrders } = partnerPortalSettings;
    const [ loading, setLoading ] = useState(true);
    const { data, saving, error } = useSelector(state => state.user);
    const user = data;
    const partnerID = localStorage.getItem("partnerID");
    const [ partner, setPartner ] = useState({});
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();
    const previousCart = usePrevious(cart);
    const cartLoading = cart.loading;

    /**
     * Formik configuration
     */
    let mutatedPartner = {
        ...partner,
        ...user,
        invoiceCompanyBusinessID:
            partner.invoiceCompanyBusinessID ? 
            partner.invoiceCompanyBusinessID : (partner.businessID ? partner.businessID : ''),
        invoiceCompany: 
            partner.invoiceCompany ? 
            partner.invoiceCompany : (partner.name ? partner.name : '')
    };

    /**
     * For lahti-cases, set person info per data found in the order
     */
    // console.log(cartOrder);
    if (userCanEditOrders) {
        mutatedPartner = {
            ...mutatedPartner,
            firstNames: cartOrder.firstNames ? cartOrder.firstNames : user.firstNames,
            lastName: cartOrder.lastName ? cartOrder.lastName : user.lastName,
            email: cartOrder.email ? cartOrder.email : user.email,
            phone: cartOrder.phone ? cartOrder.phone : user.phone
        };
    }

    const formik = useFormik({
        initialValues: {...mutatedPartner},
        enableReinitialize: true,
        validationSchema: Yup.object().shape({
            firstNames: Yup.string()
                .required(t('form.first_name_required')),
            lastName: Yup.string()
                .required(t('form.last_name_required')),
            email: Yup.string()
                .required(t('form.email_required')),
            invoiceCompany: Yup.string()
                .required(t('form.company_required')),
            invoiceCompanyBusinessID: Yup.string()
                .required(t('form.business_id_required')),
            invoiceStreetAddress: Yup.string()
                .required(t('form.street_address_required')).nullable(),
            invoicePostalCode: Yup.string()
                .required(t('form.postal_code_required')).nullable(),
            invoiceCity: Yup.string()
                .required(t('form.city_required')).nullable(),
        }),
        onSubmit: values => {
            dispatch(updateOrder(values, true, eventID, partnerPortalSettings))
        }
    });

    const { values, errors, touched } = formik;

    /**
     * Fetch partner information.
     */
    const _fetchPartner = useCallback((eventID, partnerID) => {
        dispatch(partnerFetch(partnerID));

        setLoading(true);

        api.partner.getEventPartner(eventID, partnerID).then(response => {
            setPartner(response.data.data.partners);
        }).finally(() => setLoading(false))
    }, [dispatch]);

    /**
     * Fetch partner at component mount.
     */
    useEffect(() => {
        if(partnerID) {
           _fetchPartner(eventID, partnerID)
        }
    }, [eventID, partnerID, _fetchPartner]);

    // effect to track successful order confirmation, direct to summary page
    useEffect(() => {
        if (cart.checkoutSuccess !== previousCart.checkoutSuccess) {
            if (cart.checkoutSuccess) {
                dispatch(toastrAdd({ details: t('checkout.order_success_toastr_details'), title: t('checkout.order_success_toastr_title'), type: 'success' }));
                history.push(`/events/${eventID}/orderSummary/${cartOrder.orderID}`);
            }
        }
        
        if (cart!== previousCart) {
            if (cart.error) {
                if (cart.error !== previousCart.error) {
                    dispatch(toastrAdd({
                        details: `${t('general.details')}: ${cart.error.firstError.title} - ${cart.error.firstError.details}`,
                        title: t('checkout.order_error_toastr_title'),
                        type: 'error'
                    }));
                }
            }
        }
    }, [cart, eventID, history, previousCart, cartOrder, dispatch, t]);

    const legendButton = forwardRef(({ children, onClick }, ref) => (
        <legend ref={ref}>
            {children}&nbsp;
            <span>
                <a
                    href="#"
                    style={{ color: '#000' }}
                    onClick={(e) => {
                        e.preventDefault();
                        onClick(e);
                    }}
                >
                    ({t('general.edit')})
                </a>
            </span>
        </legend>
    ));

    const _isCheckoutDisabled = () => {
        const { orderRows } = cart.order;
        const someIsInCartState = orderRows.findIndex(or => or.status === 5) > -1;
        let isDisabled = loading || saving || portalClosed || !someIsInCartState;
        return isDisabled;        
    };

    return (
        !loading ?
            <React.Fragment>
                <Row>
                    <Col md={8}>
                        <h3>{t('general.billing_details')}</h3>
                        <Form onSubmit={formik.handleSubmit} noValidate>
                            <Card>
                                <Card.Body style={{ position: 'relative' }}>
                                    {error &&
                                        <Alert variant="danger"><strong>{error.title}</strong>: {error.details}</Alert>
                                    }
                                    <fieldset>
                                        <Button
                                            as={legendButton}
                                            onClick={() => history.push('/settings')}
                                        >
                                            {t('general.person_details')}
                                        </Button>
                                        <Form.Group as={Row} controlId="firstNames">
                                            <Form.Label column sm={3}>{t('general.first_name')}</Form.Label>
                                            <Col sm={9}>
                                                <Form.Control 
                                                    type="text" 
                                                    name="firstNames" 
                                                    value={values.firstNames} 
                                                    onChange={formik.handleChange} 
                                                    isInvalid={errors.firstNames && touched.firstNames}
                                                    required />
                                                {errors.firstNames && touched.firstNames ? (
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.firstNames}
                                                    </Form.Control.Feedback>
                                                ) : null}
                                            </Col>
                                        </Form.Group>
                                        <Form.Group as={Row} controlId="lastName">
                                            <Form.Label column sm={3}>{t('general.last_name')}</Form.Label>
                                            <Col sm={9}>
                                                <Form.Control 
                                                    type="text" 
                                                    name="lastName" 
                                                    value={values.lastName} 
                                                    onChange={formik.handleChange} 
                                                    isInvalid={errors.lastName && touched.lastName}
                                                    required />
                                                {errors.lastName && touched.lastName ? (
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.lastName}
                                                    </Form.Control.Feedback>
                                                ) : null}                                            
                                            </Col>
                                        </Form.Group>
                                        <Form.Group as={Row} controlId="email">
                                            <Form.Label column sm={3}>{t('general.email')}</Form.Label>
                                            <Col sm={9}>
                                                <Form.Control 
                                                    type="text" 
                                                    name="email" 
                                                    value={values.email} 
                                                    onChange={formik.handleChange} 
                                                    isInvalid={errors.email && touched.email}
                                                    required />
                                                {errors.email && touched.email ? (
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.email}
                                                    </Form.Control.Feedback>
                                                ) : null}  
                                            </Col>
                                        </Form.Group>
                                        <Form.Group as={Row} controlId="phone">
                                            <Form.Label column sm={3}>{t('general.phone_number')}</Form.Label>
                                            <Col sm={9}>
                                                <Form.Control 
                                                    type="text" 
                                                    name="phone" 
                                                    value={values.phone || ""} 
                                                    onChange={formik.handleChange} 
                                                    isInvalid={errors.phone && touched.phone}
                                                    required />
                                                {errors.phone && touched.phone ? (
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.phone}
                                                    </Form.Control.Feedback>
                                                ) : null}  
                                            </Col>
                                        </Form.Group>
                                    </fieldset>
                                    <fieldset>
                                        <Button
                                            as={legendButton}
                                            onClick={() => history.push('/settings/organization')}
                                        >
                                            {t('checkout.organization_details')}
                                        </Button>
                                        <Form.Group as={Row} controlId="invoiceCompany">
                                            <Form.Label column sm={3}>{t('general.company')}</Form.Label>
                                            <Col sm={9}>
                                                <Form.Control 
                                                    type="text" 
                                                    name="invoiceCompany" 
                                                    value={values.invoiceCompany || ''} 
                                                    onChange={formik.handleChange} 
                                                    isInvalid={errors.invoiceCompany && touched.invoiceCompany}
                                                    required />
                                                {errors.invoiceCompany && touched.invoiceCompany ? (
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.invoiceCompany}
                                                    </Form.Control.Feedback>
                                                ) : null}  
                                            </Col>
                                        </Form.Group>
                                        <Form.Group as={Row} controlId="invoiceCompanyBusinessID">
                                            <Form.Label column sm={3}>{t('general.business_id')}</Form.Label>
                                            <Col sm={9}>
                                                <Form.Control 
                                                    type="text" 
                                                    name="invoiceCompanyBusinessID" 
                                                    value={values.invoiceCompanyBusinessID || ''}
                                                    onChange={formik.handleChange} 
                                                    isInvalid={errors.invoiceCompanyBusinessID && touched.invoiceCompanyBusinessID}
                                                    required />
                                                {errors.invoiceCompanyBusinessID && touched.invoiceCompanyBusinessID ? (
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.invoiceCompanyBusinessID}
                                                    </Form.Control.Feedback>
                                                ) : null}  
                                            </Col>
                                        </Form.Group>
                                        <Form.Group as={Row} controlId="invoiceStreetAddress">
                                            <Form.Label column sm={3}>{t('general.street_address')}</Form.Label>
                                            <Col sm={9}>
                                                <Form.Control 
                                                    type="text" 
                                                    name="invoiceStreetAddress" 
                                                    value={values.invoiceStreetAddress || ""} 
                                                    onChange={formik.handleChange} 
                                                    isInvalid={errors.invoiceStreetAddress && touched.invoiceStreetAddress}
                                                    required />
                                                {errors.invoiceStreetAddress && touched.invoiceStreetAddress ? (
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.invoiceStreetAddress}
                                                    </Form.Control.Feedback>
                                                ) : null}  
                                            </Col>
                                        </Form.Group>
                                        <Form.Group as={Row} controlId="invoicePostalCode">
                                            <Form.Label column sm={3}>{t('general.postal_code')}</Form.Label>
                                            <Col sm={2}>
                                                <Form.Control 
                                                    type="text" 
                                                    name="invoicePostalCode" 
                                                    value={values.invoicePostalCode || ""} 
                                                    onChange={formik.handleChange} 
                                                    isInvalid={errors.invoicePostalCode && touched.invoicePostalCode}
                                                    required />
                                                {errors.invoicePostalCode && touched.invoicePostalCode ? (
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.invoicePostalCode}
                                                    </Form.Control.Feedback>
                                                ) : null}  
                                            </Col>
                                        </Form.Group>
                                        <Form.Group as={Row} controlId="invoiceCity">
                                            <Form.Label column sm={3}>{t('general.city')}</Form.Label>
                                            <Col sm={7}>
                                                <Form.Control 
                                                    type="text" 
                                                    name="invoiceCity" 
                                                    value={values.invoiceCity || ""} 
                                                    onChange={formik.handleChange} 
                                                    isInvalid={errors.invoiceCity && touched.invoiceCity}
                                                    required />
                                                {errors.invoiceCity && touched.invoiceCity ? (
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.invoiceCity}
                                                    </Form.Control.Feedback>
                                                ) : null}  
                                            </Col>
                                        </Form.Group>
                                        <Form.Group as={Row} controlId="vatNumber">
                                            <Form.Label column sm={3}>{t('general.vat')}</Form.Label>
                                            <Col sm={5}>
                                                <Form.Control 
                                                    type="text" 
                                                    name="vatNumber" 
                                                    value={values.vatNumber || ""} 
                                                    onChange={formik.handleChange} 
                                                    isInvalid={errors.varNumber && touched.vatNumber}
                                                    required />
                                                {errors.vatNumber && touched.vatNumber ? (
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.vatNumber}
                                                    </Form.Control.Feedback>
                                                ) : null}  
                                            </Col>
                                        </Form.Group>
                                        <Form.Group as={Row} controlId="eInvoiceAddress">
                                            <Form.Label column sm={3}>{t('general.einvoice_address')}</Form.Label>
                                            <Col sm={5}>
                                                <Form.Control 
                                                    type="text" 
                                                    name="eInvoiceAddress" 
                                                    value={values.eInvoiceAddress || ""} 
                                                    onChange={formik.handleChange} 
                                                    isInvalid={errors.eInvoiceAddress && touched.eInvoiceAddress}
                                                    required />
                                                {errors.eInvoiceAddress && touched.eInvoiceAddress ? (
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.eInvoiceAddress}
                                                    </Form.Control.Feedback>
                                                ) : null}  
                                            </Col>
                                        </Form.Group>
                                        <Form.Group as={Row} controlId="eInvoiceOperator">
                                            <Form.Label column sm={3}>{t('general.einvoice_operator')}</Form.Label>
                                            <Col sm={5}>
                                                <Form.Control 
                                                    type="text" 
                                                    name="eInvoiceOperator" 
                                                    value={values.eInvoiceOperator || ""} 
                                                    onChange={formik.handleChange} 
                                                    isInvalid={errors.eInvoiceOperator && touched.eInvoiceOperator}
                                                    required />
                                                {errors.eInvoiceOperator && touched.eInvoiceOperator ? (
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.eInvoiceOperator}
                                                    </Form.Control.Feedback>
                                                ) : null}  
                                            </Col>
                                        </Form.Group>
                                        <Form.Group as={Row} controlId="eInvoiceIntermediateId">
                                            <Form.Label column sm={3}>{t('general.einvoice_intermediate_id')}</Form.Label>
                                            <Col sm={5}>
                                                <Form.Control 
                                                    type="text" 
                                                    name="eInvoiceIntermediateId" 
                                                    value={values.eInvoiceIntermediateId || ""} 
                                                    onChange={formik.handleChange} 
                                                    isInvalid={errors.eInvoiceIntermediateId && touched.eInvoiceIntermediateId}
                                                    required />
                                                {errors.eInvoiceIntermediateId && touched.eInvoiceIntermediateId ? (
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.eInvoiceIntermediateId}
                                                    </Form.Control.Feedback>
                                                ) : null}  
                                            </Col>
                                        </Form.Group>
                                    </fieldset>
                                {cartLoading ?
                                    <LoadingOverlay />
                                : null}
                                </Card.Body>

                                <Card.Footer>
                                    <Form.Group as={Row}>
                                        <Col sm={{span: 9, offset: 3}}>
                                                <Button type="submit" variant="success" disabled={_isCheckoutDisabled()}>{t('checkout.accept_order')}</Button>
                                        </Col>
                                    </Form.Group>
                                </Card.Footer>
                            </Card>
                            </Form>

                    </Col>
                    <Col md={4}>
                        <h3>{t('checkout.order_products')}</h3>
                        <Card className="cartContent">
                            <CartContents 
                                cartOrder={cartOrder} 
                                orderRowsMeta={orderRowsMeta}
                                removeProductFromCart={removeProductFromCart}
                                setProductQuantity={setProductQuantity}
                                partnerPortalSettings={partnerPortalSettings}
                                checkout={true}>
                            </CartContents>
                        </Card>
                    </Col>
                </Row>
            </React.Fragment>
        :
        <div className="text-center" style={{ padding: '30px'}}>
            <Spinner animation="border" role="status" variant="primary">
                <span className="sr-only">{t('general.loading')}</span>
            </Spinner>
        </div>
    )
}

export default ShopHOC(Checkout);